/**
 * One stroke icon component, one visual weight.
 *
 * Paths are inline rather than a package: the console needs nine glyphs, and a
 * dependency that ships a thousand is a dependency whose bundle you cannot see.
 */
export function Icon({ d, className = 'h-[1.125rem] w-[1.125rem]' }: { d: string; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {d.split('|').map((segment) => (
        <path key={segment} d={segment} />
      ))}
    </svg>
  );
}

export const ICONS = {
  /** A board with rows — the overview reads as a departures board. */
  overview: 'M3 5h18M3 12h18M3 19h11|M17 17l2.5 2.5L23 16',
  /** A coach, seen head-on. */
  operators: 'M4 17V7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10|M4 12h16|M7 20v-2m10 2v-2|M7.5 15h.01M16.5 15h.01',
  /** A document with a currency rule. */
  invoices: 'M6 3h9l4 4v14H6z|M15 3v4h4|M9.5 12h5|M9.5 15.5h5|M12 10.5v7',
  /** An eye over a timeline. */
  audit: 'M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z|M12 14.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  /** Two figures. */
  staff: 'M15.5 20v-1.6a3.4 3.4 0 0 0-3.4-3.4H6.9A3.4 3.4 0 0 0 3.5 18.4V20|M9.5 11.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7|M20.5 20v-1.6a3.4 3.4 0 0 0-2.6-3.3|M16 4.6a3.4 3.4 0 0 1 0 6.6',
  /** A shield with a check — what a role is permitted to do. */
  roles: 'M12 3l7 3v5c0 5.2-3 8.3-7 10-4-1.7-7-4.8-7-10V6z|M9.3 12.3l1.9 1.9 3.3-3.6',
  /** Two stops joined by a road that bends — origin to destination. */
  route: 'M6 20a3 3 0 1 0 0-6 3 3 0 0 0 0 6z|M18 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6z|M18 10v2a4 4 0 0 1-4 4h-4a4 4 0 0 0-4 2',
  trips: 'M5 17h14M7 17V9a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v8|M4 12h16|M8 20v-2m8 2v-2',
  bookings: 'M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 0 0 6v3a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-3a2 2 0 0 0 0-6z|M12 8v8',
  disputes: 'M12 9v4m0 4h.01|M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z',
  stops: 'M12 21s-7-5.1-7-11a7 7 0 0 1 14 0c0 5.9-7 11-7 11z|M12 12a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
  fleet: 'M3 17V8a2 2 0 0 1 2-2h9l5 5v6|M3 12h16|M6 20v-3m11 3v-3',
  timetables: 'M8 2v4M16 2v4M3 9h18|M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z|M12 13v4l2 1',
  promotions: 'M3.5 11.2V4.5a1 1 0 0 1 1-1h6.7a1 1 0 0 1 .7.3l8 8a1 1 0 0 1 0 1.4l-6.7 6.7a1 1 0 0 1-1.4 0l-8-8a1 1 0 0 1-.3-.7z',
  branding: 'M12 3l2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 18.9 6.1 21l1.2-6.5L2.5 9.9 9.1 9z',
  settings: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z|M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 7 19.4a1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0-1.1-2.7H1a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 2.6 7a1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H7a1.6 1.6 0 0 0 1-1.5V1a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 2.7 1.1 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V7a1.6 1.6 0 0 0 1.5 1H23a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z',
  billing: 'M6 3h9l4 4v14H6z|M15 3v4h4|M9.5 12h5|M9.5 15.5h5',
  bell: 'M18 8.5a6 6 0 1 0-12 0c0 5-2.2 6.5-2.2 6.5h16.4S18 13.5 18 8.5z|M13.7 19a2 2 0 0 1-3.4 0',
  globe: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z|M3.5 9h17M3.5 15h17|M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18z',
  signOut: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4|M16 17l5-5-5-5|M21 12H9',
  /** Arrow leaving a frame — opens in a new tab. */
  external: 'M14 4h6v6|M20 4l-9 9|M18 13v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h5',
  menu: 'M4 7h16M4 12h16M4 17h16',
  close: 'M6 6l12 12M18 6L6 18',
  chevron: 'M9 6l6 6-6 6',
  check: 'M5 12.5l4.5 4.5L19 7',
  user: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z|M4.5 20a7.5 7.5 0 0 1 15 0',
} as const;
